// Primary 5 English Learning Data - Part 1 (Topics 1-10)
// Based on Primary English Learning P5 Topics.txt

export const p5DataPart1 = {
  gradeId: 'p5',
  gradeName: 'Primary 5 (小學五年級)',
  objectives: [
    'Build on Primary 4 foundations with advanced grammar structures',
    'Develop sophisticated reading comprehension and writing skills',
    'Learn complex verb forms and advanced tenses',
    'Master advanced vocabulary and expressions',
    'Understand complex sentence structures and discourse markers'
  ],
  keySkills: [
    'Advanced tenses and verb forms',
    'Complex sentence structures',
    'Advanced vocabulary and expressions',
    'Discourse markers and connectors',
    'Sophisticated writing techniques'
  ],
  topics: [
    {
      id: 'p5-topic-1',
      title: 'Present Perfect Continuous',
      description: 'Learning to use present perfect continuous tense',
      purpose: 'To teach students how to form and use present perfect continuous tense to describe actions that started in the past and are still continuing.',
      questions: [
        {
          question: 'I _____ (study) English for three years.',
          answer: 'have been studying',
          explanation: 'Use "have/has been + verb-ing" for present perfect continuous.'
        },
        {
          question: 'She _____ (work) at this company since 2020.',
          answer: 'has been working',
          explanation: 'Use "has been + verb-ing" for third person singular.'
        },
        {
          question: 'They _____ (live) in Hong Kong for five years.',
          answer: 'have been living',
          explanation: 'Use "have been + verb-ing" for plural subjects.'
        },
        {
          question: 'He _____ (teach) English for ten years.',
          answer: 'has been teaching',
          explanation: 'Use "has been + verb-ing" for third person singular.'
        },
        {
          question: 'We _____ (wait) for the bus for thirty minutes.',
          answer: 'have been waiting',
          explanation: 'Use "have been + verb-ing" for "we".'
        },
        {
          question: 'The children _____ (play) in the garden all afternoon.',
          answer: 'have been playing',
          explanation: 'Use "have been + verb-ing" for plural subjects.'
        },
        {
          question: 'I _____ (read) this book for two hours.',
          answer: 'have been reading',
          explanation: 'Use "have been + verb-ing" for "I".'
        },
        {
          question: 'She _____ (cook) dinner since 6 PM.',
          answer: 'has been cooking',
          explanation: 'Use "has been + verb-ing" for third person singular.'
        },
        {
          question: 'They _____ (build) this house for six months.',
          answer: 'have been building',
          explanation: 'Use "have been + verb-ing" for plural subjects.'
        },
        {
          question: 'He _____ (practice) piano for two hours.',
          answer: 'has been practicing',
          explanation: 'Use "has been + verb-ing" for third person singular.'
        }
      ]
    },
    {
      id: 'p5-topic-2',
      title: 'Past Perfect Tense',
      description: 'Learning to use past perfect tense to show sequence of events',
      purpose: 'To teach students how to use past perfect tense to show that one action happened before another action in the past.',
      questions: [
        {
          question: 'I _____ (finish) my homework before dinner.',
          answer: 'had finished',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'She _____ (arrive) at the station before the train left.',
          answer: 'had arrived',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'They _____ (eat) dinner before the movie started.',
          answer: 'had eaten',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'He _____ (study) English before he moved to England.',
          answer: 'had studied',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'We _____ (book) the tickets before the concert sold out.',
          answer: 'had booked',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'The movie _____ (start) before we arrived.',
          answer: 'had started',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'I _____ (never/see) such a beautiful sunset before.',
          answer: 'had never seen',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'She _____ (already/leave) when I called.',
          answer: 'had already left',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'They _____ (visit) Paris before they went to London.',
          answer: 'had visited',
          explanation: 'Use "had + past participle" for past perfect tense.'
        },
        {
          question: 'He _____ (work) there for five years before he quit.',
          answer: 'had worked',
          explanation: 'Use "had + past participle" for past perfect tense.'
        }
      ]
    },
    {
      id: 'p5-topic-3',
      title: 'Future Perfect Tense',
      description: 'Learning to use future perfect tense to describe completed future actions',
      purpose: 'To teach students how to use future perfect tense to describe actions that will be completed by a specific time in the future.',
      questions: [
        {
          question: 'By next week, I _____ (finish) reading this book.',
          answer: 'will have finished',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By the end of the year, she _____ (graduate) from university.',
          answer: 'will have graduated',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By tomorrow, they _____ (complete) the project.',
          answer: 'will have completed',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By next month, he _____ (work) here for ten years.',
          answer: 'will have worked',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By Friday, we _____ (finish) all our homework.',
          answer: 'will have finished',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By the end of the day, I _____ (write) five emails.',
          answer: 'will have written',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By next summer, she _____ (learn) three languages.',
          answer: 'will have learned',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By the time you arrive, I _____ (prepare) dinner.',
          answer: 'will have prepared',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By the end of the week, they _____ (visit) all the museums.',
          answer: 'will have visited',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        },
        {
          question: 'By next year, he _____ (save) enough money for a car.',
          answer: 'will have saved',
          explanation: 'Use "will have + past participle" for future perfect tense.'
        }
      ]
    },
    {
      id: 'p5-topic-4',
      title: 'Conditional Sentences (Second)',
      description: 'Learning to use second conditional sentences for hypothetical situations',
      purpose: 'To teach students how to form and use second conditional sentences to express hypothetical or unlikely situations.',
      questions: [
        {
          question: 'If I _____ (have) a million dollars, I would travel the world.',
          answer: 'had',
          explanation: 'Use past simple in the if-clause of second conditional.'
        },
        {
          question: 'If she _____ (study) harder, she would pass the exam.',
          answer: 'studied',
          explanation: 'Use past simple in the if-clause of second conditional.'
        },
        {
          question: 'If they _____ (live) in Hong Kong, they would speak Cantonese.',
          answer: 'lived',
          explanation: 'Use past simple in the if-clause of second conditional.'
        },
        {
          question: 'If he _____ (be) rich, he would buy a big house.',
          answer: 'were',
          explanation: 'Use "were" for "be" in second conditional (even with "he").'
        },
        {
          question: 'If we _____ (have) more time, we would visit the museum.',
          answer: 'had',
          explanation: 'Use past simple in the if-clause of second conditional.'
        },
        {
          question: 'If I _____ (know) the answer, I would tell you.',
          answer: 'knew',
          explanation: 'Use past simple in the if-clause of second conditional.'
        },
        {
          question: 'If she _____ (speak) French, she would work in Paris.',
          answer: 'spoke',
          explanation: 'Use past simple in the if-clause of second conditional.'
        },
        {
          question: 'If they _____ (be) younger, they would learn to ski.',
          answer: 'were',
          explanation: 'Use "were" for "be" in second conditional.'
        },
        {
          question: 'If he _____ (like) cooking, he would become a chef.',
          answer: 'liked',
          explanation: 'Use past simple in the if-clause of second conditional.'
        },
        {
          question: 'If I _____ (can) fly, I would visit every country.',
          answer: 'could',
          explanation: 'Use "could" (past of "can") in second conditional.'
        }
      ]
    },
    {
      id: 'p5-topic-5',
      title: 'Passive Voice (Advanced)',
      description: 'Learning to use passive voice in different tenses',
      purpose: 'To teach students how to form and use passive voice in various tenses including present perfect, past perfect, and future tenses.',
      questions: [
        {
          question: 'The letter _____ (write) by John yesterday.',
          answer: 'was written',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'The house _____ (build) by workers last year.',
          answer: 'was built',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'The book _____ (read) by many students.',
          answer: 'is read',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The movie _____ (watch) by millions of people.',
          answer: 'is watched',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The cake _____ (bake) by my mother.',
          answer: 'was baked',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'English _____ (speak) in many countries.',
          answer: 'is spoken',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The song _____ (sing) by the choir.',
          answer: 'is sung',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The picture _____ (paint) by a famous artist.',
          answer: 'was painted',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'The letter _____ (send) yesterday.',
          answer: 'was sent',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'The car _____ (wash) by the mechanic.',
          answer: 'was washed',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        }
      ]
    },
    {
      id: 'p5-topic-6',
      title: 'Reported Speech (Advanced)',
      description: 'Learning to change complex direct speech to reported speech',
      purpose: 'To teach students how to change complex direct speech to reported speech, including questions, commands, and statements with various tenses.',
      questions: [
        {
          question: 'Direct: "I am studying." → Reported: He said he _____ studying.',
          answer: 'was',
          explanation: 'Change present continuous to past continuous in reported speech.'
        },
        {
          question: 'Direct: "I have finished my homework." → Reported: She said she _____ finished her homework.',
          answer: 'had',
          explanation: 'Change present perfect to past perfect in reported speech.'
        },
        {
          question: 'Direct: "I will help you." → Reported: He said he _____ help me.',
          answer: 'would',
          explanation: 'Change "will" to "would" in reported speech.'
        },
        {
          question: 'Direct: "I can swim." → Reported: She said she _____ swim.',
          answer: 'could',
          explanation: 'Change "can" to "could" in reported speech.'
        },
        {
          question: 'Direct: "I went to the park." → Reported: He said he _____ to the park.',
          answer: 'had gone',
          explanation: 'Change simple past to past perfect in reported speech.'
        },
        {
          question: 'Direct: "I must go now." → Reported: She said she _____ go then.',
          answer: 'had to',
          explanation: 'Change "must" to "had to" in reported speech.'
        },
        {
          question: 'Direct: "I am tired." → Reported: He said he _____ tired.',
          answer: 'was',
          explanation: 'Change present tense to past tense in reported speech.'
        },
        {
          question: 'Direct: "I like ice cream." → Reported: She said she _____ ice cream.',
          answer: 'liked',
          explanation: 'Change present tense to past tense in reported speech.'
        },
        {
          question: 'Direct: "I have been working hard." → Reported: He said he _____ working hard.',
          answer: 'had been',
          explanation: 'Change present perfect continuous to past perfect continuous in reported speech.'
        },
        {
          question: 'Direct: "I will visit you tomorrow." → Reported: She said she _____ visit me the next day.',
          answer: 'would',
          explanation: 'Change "will" to "would" and "tomorrow" to "the next day" in reported speech.'
        }
      ]
    },
    {
      id: 'p5-topic-7',
      title: 'Modal Verbs (Advanced)',
      description: 'Learning to use advanced modal verbs and their perfect forms',
      purpose: 'To teach students how to use advanced modal verbs including perfect forms (modal + have + past participle) to express past possibility, certainty, and obligation.',
      questions: [
        {
          question: 'You _____ (study) harder for the exam.',
          answer: 'should have studied',
          explanation: 'Use "should have + past participle" to express regret about past actions.'
        },
        {
          question: 'He _____ (arrive) by now.',
          answer: 'must have arrived',
          explanation: 'Use "must have + past participle" to express certainty about past actions.'
        },
        {
          question: 'She _____ (forgot) about the meeting.',
          answer: 'might have forgotten',
          explanation: 'Use "might have + past participle" to express possibility about past actions.'
        },
        {
          question: 'They _____ (left) early this morning.',
          answer: 'could have left',
          explanation: 'Use "could have + past participle" to express possibility about past actions.'
        },
        {
          question: 'I _____ (taken) the bus instead of walking.',
          answer: 'should have taken',
          explanation: 'Use "should have + past participle" to express regret about past actions.'
        },
        {
          question: 'He _____ (been) at home when I called.',
          answer: 'must have been',
          explanation: 'Use "must have + past participle" to express certainty about past actions.'
        },
        {
          question: 'She _____ (lost) her keys.',
          answer: 'might have lost',
          explanation: 'Use "might have + past participle" to express possibility about past actions.'
        },
        {
          question: 'They _____ (finished) the work by now.',
          answer: 'should have finished',
          explanation: 'Use "should have + past participle" to express expectation about past actions.'
        },
        {
          question: 'He _____ (gone) to the store.',
          answer: 'could have gone',
          explanation: 'Use "could have + past participle" to express possibility about past actions.'
        },
        {
          question: 'She _____ (forgotten) to lock the door.',
          answer: 'must have forgotten',
          explanation: 'Use "must have + past participle" to express certainty about past actions.'
        }
      ]
    },
    {
      id: 'p5-topic-8',
      title: 'Discourse Markers',
      description: 'Learning to use discourse markers to connect ideas and show relationships',
      purpose: 'To teach students how to use discourse markers to connect ideas, show relationships between sentences, and improve the flow of writing and speaking.',
      questions: [
        {
          question: 'Complete: "I like swimming. _____, I also enjoy running."',
          answer: 'Furthermore',
          explanation: 'Use "Furthermore" to add more information to what was said before.'
        },
        {
          question: 'Fill in the blank: "The weather was bad. _____, we decided to stay home."',
          answer: 'Therefore',
          explanation: 'Use "Therefore" to show the result or consequence of what was said before.'
        },
        {
          question: 'Complete: "I studied hard. _____, I passed the exam."',
          answer: 'Consequently',
          explanation: 'Use "Consequently" to show the result of what was said before.'
        },
        {
          question: 'Fill in the blank: "He was tired. _____, he continued working."',
          answer: 'However',
          explanation: 'Use "However" to show contrast with what was said before.'
        },
        {
          question: 'Complete: "The food was delicious. _____, the service was excellent."',
          answer: 'Moreover',
          explanation: 'Use "Moreover" to add more information to what was said before.'
        },
        {
          question: 'Fill in the blank: "It was raining. _____, we went for a walk."',
          answer: 'Nevertheless',
          explanation: 'Use "Nevertheless" to show contrast despite what was said before.'
        },
        {
          question: 'Complete: "She was sick. _____, she went to school."',
          answer: 'However',
          explanation: 'Use "However" to show contrast with what was said before.'
        },
        {
          question: 'Fill in the blank: "He failed the test. _____, he didn\'t study."',
          answer: 'This is because',
          explanation: 'Use "This is because" to explain the reason for what was said before.'
        },
        {
          question: 'Complete: "The movie was long. _____, it was very interesting."',
          answer: 'However',
          explanation: 'Use "However" to show contrast with what was said before.'
        },
        {
          question: 'Fill in the blank: "She worked hard. _____, she got promoted."',
          answer: 'As a result',
          explanation: 'Use "As a result" to show the consequence of what was said before.'
        }
      ]
    },
    {
      id: 'p5-topic-9',
      title: 'Phrasal Verbs (Advanced)',
      description: 'Learning to use advanced phrasal verbs with multiple meanings',
      purpose: 'To teach students how to use advanced phrasal verbs that have multiple meanings and can be used in different contexts.',
      questions: [
        {
          question: 'I need to _____ my homework before dinner.',
          answer: 'get through',
          explanation: 'Use "get through" to mean complete or finish something.'
        },
        {
          question: 'She _____ her grandmother every weekend.',
          answer: 'looks after',
          explanation: 'Use "look after" to mean take care of someone.'
        },
        {
          question: 'He _____ his old clothes to charity.',
          answer: 'gave away',
          explanation: 'Use "give away" to mean donate or give something for free.'
        },
        {
          question: 'We _____ the meeting until next week.',
          answer: 'put off',
          explanation: 'Use "put off" to mean postpone or delay something.'
        },
        {
          question: 'She _____ her keys in the car.',
          answer: 'left behind',
          explanation: 'Use "leave behind" to mean forget to take something with you.'
        },
        {
          question: 'He _____ his job last month.',
          answer: 'gave up',
          explanation: 'Use "give up" to mean quit or stop doing something.'
        },
        {
          question: 'They _____ the truth about the accident.',
          answer: 'found out',
          explanation: 'Use "find out" to mean discover or learn something.'
        },
        {
          question: 'I _____ my friend at the airport.',
          answer: 'picked up',
          explanation: 'Use "pick up" to mean collect someone from a place.'
        },
        {
          question: 'She _____ her coat before entering.',
          answer: 'took off',
          explanation: 'Use "take off" to mean remove clothing.'
        },
        {
          question: 'He _____ his homework quickly.',
          answer: 'got through',
          explanation: 'Use "get through" to mean complete or finish something.'
        }
      ]
    },
    {
      id: 'p5-topic-10',
      title: 'Complex Sentence Structures',
      description: 'Learning to construct complex sentences with multiple clauses',
      purpose: 'To teach students how to construct complex sentences that contain multiple clauses, including relative clauses, adverbial clauses, and noun clauses.',
      questions: [
        {
          question: 'Complete: "The man _____ lives next door is a doctor."',
          answer: 'who',
          explanation: 'Use "who" to introduce a relative clause about a person.'
        },
        {
          question: 'Fill in the blank: "I will call you _____ I arrive."',
          answer: 'when',
          explanation: 'Use "when" to introduce a time clause.'
        },
        {
          question: 'Complete: "The book _____ I bought is very interesting."',
          answer: 'which',
          explanation: 'Use "which" to introduce a relative clause about a thing.'
        },
        {
          question: 'Fill in the blank: "I will help you _____ you need it."',
          answer: 'if',
          explanation: 'Use "if" to introduce a conditional clause.'
        },
        {
          question: 'Complete: "The car _____ is parked outside is red."',
          answer: 'that',
          explanation: 'Use "that" to introduce a relative clause about a thing.'
        },
        {
          question: 'Fill in the blank: "I will stay home _____ it rains."',
          answer: 'if',
          explanation: 'Use "if" to introduce a conditional clause.'
        },
        {
          question: 'Complete: "The girl _____ is wearing a blue dress is my sister."',
          answer: 'who',
          explanation: 'Use "who" to introduce a relative clause about a person.'
        },
        {
          question: 'Fill in the blank: "I will wait _____ you finish."',
          answer: 'until',
          explanation: 'Use "until" to introduce a time clause.'
        },
        {
          question: 'Complete: "The movie _____ we watched was exciting."',
          answer: 'which',
          explanation: 'Use "which" to introduce a relative clause about a thing.'
        },
        {
          question: 'Fill in the blank: "I will go _____ you come with me."',
          answer: 'if',
          explanation: 'Use "if" to introduce a conditional clause.'
        }
      ]
    }
  ]
}; 